import axios from "axios";
import { Actor, HttpAgent } from "@dfinity/agent";
import {
    Ed25519KeyIdentity,
    Delegation,
    DelegationChain,
    DelegationIdentity,
} from "@dfinity/identity";

import { wallet } from "./idlFactories.js"; // Adjust path as needed

const canisterIds = {
    siwbCanisterId: "bcxqa-kqaaa-aaaak-qotba-cai",
    fastbtcCanisterId: "ztwhb-qiaaa-aaaaj-azw7a-cai",
    ckbtcLedgerCanisterId: "mxzaz-hqaaa-aaaar-qaada-cai",
    ckbtcMinterCanisterId: "mqygn-kiaaa-aaaar-qaadq-cai",
    voltCanisterId: "aclt4-uaaaa-aaaak-qb4zq-cai",
};

class IPCAuth {
    constructor() {
        // No private key is stored since the user signs on the client side.
        this.anonimousAgent = null;
        this.anonimousActor = null;
        this.identity = null;
        this.bearerToken = null;
    }

    // Helper to convert a byte array into a Base64-encoded string.
    _bytesToBase64(bytes) {
        const binString = String.fromCodePoint(...bytes);
        return btoa(binString);
    }

    // Verifies that the provided signature is valid for the given message and address.
    _verifyMessage(address, message, signature) {
        // If message is a Uint8Array, decode it to a string.
        const decodedMessage =
            typeof message === "string" ? message : new TextDecoder().decode(message);

        // Convert the signature bytes to a Base64 string.
        const signatureBase64 = this._bytesToBase64(signature);

        // Use bip322-js Verifier to verify the signature.
        return Verifier.verifySignature(address, decodedMessage, signatureBase64);
    }

    // (Optional) Generates a random Bitcoin address. This can be used if an anonymous login is desired.
    generateRadomBtcAddress() {
        const keyPair = ECPair.makeRandom();
        const publicKeyBuffer = Buffer.from(keyPair.publicKey);
        const { address } = bitcoin.payments.p2wpkh({ pubkey: publicKeyBuffer });
        console.log("Bitcoin Address:", address);
        console.log("Public Key (hex):", publicKeyBuffer.toString("hex"));
        console.log("Private Key (WIF):", keyPair.toWIF());
        return {
            address,
            privateKey: keyPair.toWIF(),
            publicKey: publicKeyBuffer.toString("hex"),
            keyPair,
        };
    }

    getAddressInfoFromPrivateKey(wif) {
        // Select the network (bitcoin.networks.bitcoin for mainnet, bitcoin.networks.testnet for testnet)
        const network = bitcoin.networks.bitcoin;

        // Re-create the key pair from the WIF
        const keyPair = ECPair.fromWIF(wif, network);

        // Get the public key (already a Buffer)
        const publicKeyBuffer = Buffer.from(keyPair.publicKey);

        // Generate a native SegWit (Bech32) address using p2wpkh
        const { address: segwitAddress } = bitcoin.payments.p2wpkh({
            pubkey: publicKeyBuffer,
            network,
        });

        console.log('Native SegWit (Bech32) Address:', segwitAddress);
        console.log('Public Key (hex):', publicKeyBuffer.toString('hex'));

        return {
            address: segwitAddress,
            publicKey: publicKeyBuffer.toString('hex'),
            privateKey: wif,
            keyPair
        };
    }



    getAddressInfoFromMnemonic(mnemonic, derivationPath = "m/49'/0'/0'/0/0", network = bitcoin.networks.bitcoin) {
        console.log(`\n--- Deriving for Mnemonic (Path: ${ derivationPath || "Default" }, Network: ${ network === bitcoin.networks.testnet ? 'Testnet' : 'Mainnet' }) ---`);

        // --- 1. Validate Mnemonic ---
        if (!bip39.validateMnemonic(mnemonic)) {
            throw new Error('Invalid mnemonic phrase provided.');
        }
        console.log("Mnemonic validated.");

        // --- 2. Mnemonic to Seed ---
        const seed = bip39.mnemonicToSeedSync(mnemonic); // Use async version if preferred: await bip39.mnemonicToSeed(mnemonic)
        console.log("Seed generated from mnemonic.");

        // --- 3. Seed to Master Node ---
        const root = bip32.fromSeed(seed, network);
        console.log("BIP32 root node created.");

        // --- 4. Derive Child Node ---
        const path = derivationPath || "m/84'/0'/0'/0/0"; // Standard P2WPKH path
        const childNode = root.derivePath(path);
        console.log(`Derived child node using path: ${ path }`);

        // --- 5. Extract Keys and **Apply Buffer Fix** ---

        // Get the public key (might be Uint8Array, needs conversion)
        const derivedPubKey = childNode.publicKey;
        if (!derivedPubKey) {
            throw new Error(`Could not derive public key for path ${ path }.`);
        }
        console.log(`Raw derived public key type: ${ Buffer.isBuffer(derivedPubKey) ? 'Buffer' : 'Uint8Array' }, Length: ${ derivedPubKey.length }`);

        // ** CRITICAL FIX: Ensure publicKeyBuffer is a Buffer instance **
        const publicKeyBuffer = Buffer.isBuffer(derivedPubKey)
            ? derivedPubKey // If it's already a buffer, use it directly
            : Buffer.from(derivedPubKey.buffer, derivedPubKey.byteOffset, derivedPubKey.byteLength); // Otherwise, convert from Uint8Array

        console.log(`Ensured publicKeyBuffer is Buffer: ${ Buffer.isBuffer(publicKeyBuffer) }, Length: ${ publicKeyBuffer.length }`);


        // Get the private key (Buffer)
        const privateKeyBuffer = childNode.privateKey;
        if (!privateKeyBuffer) {
            throw new Error(`Could not derive private key for path ${ path }. Check path format and library versions.`);
        }
        console.log("Private key buffer extracted.");

        // --- 6. Generate Native SegWit (Bech32/P2WPKH) Address ---
        // Use the derived (and correctly buffered) public key to generate the address.
        const { address: segwitAddress } = bitcoin.payments.p2wpkh({
            pubkey: publicKeyBuffer, // Pass the verified Buffer instance
            network,
        });
        console.log("Native SegWit (Bech32) address generated.");

        // --- 7. Get Private Key in WIF ---
        const wif = childNode.toWIF();
        console.log("Private key converted to WIF.");

        // --- Log Output (Final Values) ---
        console.log('------------------------------------------');
        console.log('Derivation Path Used:', path);
        console.log('Native SegWit (Bech32) Address:', segwitAddress);
        console.log('Public Key (hex):', publicKeyBuffer.toString('hex'));
        console.log('Private Key (WIF):', wif);
        console.log('------------------------------------------');

        // --- 8. Return Results ---
        return {
            address: segwitAddress,
            publicKey: publicKeyBuffer.toString('hex'),
            privateKey: wif, // Return the specific WIF for this derived address
            derivedNode: childNode, // The BIP32 node object for this specific path
            derivationPath: path
        };
    }


    async initializAnonimousActor() {
        this.anonimousAgent = new HttpAgent({
            host: "https://ic0.app",
            identity: Ed25519KeyIdentity.generate(),
        });
        // For non-production, fetch the root key.
        if (process.env.NODE_ENV !== "production") {
            await this.anonimousAgent.fetchRootKey();
        }
        this.anonimousActor = Actor.createActor(wallet, {
            agent: this.anonimousAgent,
            canisterId: canisterIds.siwbCanisterId,
        });
    }

    _verifyMessage(address, message, signature) {
        // If message is a Uint8Array, decode it to a string.
        const decodedMessage =
            typeof message === "string" ? message : new TextDecoder().decode(message);

        // Convert the signature bytes to a Base64 string.
        const signatureBase64 = this._bytesToBase64(signature);

        // Use bip322-js Verifier to verify the signature.
        return Verifier.verifySignature(address, decodedMessage, signatureBase64);
    }

    _signMessage(keyWIF, message, network = bitcoin.networks.bitcoin) {
        // Create key pair from the provided WIF.
        const keyPair = ECPair.fromWIF(keyWIF, network);
        const privateKey = keyPair.privateKey; // 32-byte Buffer.
        const compressed = keyPair.compressed;   // Boolean indicating if the public key is compressed.

        // Helper: Double SHA256 hash.
        function hash256(buffer) {
            return createHash('sha256')
                .update(createHash('sha256').update(buffer).digest())
                .digest();
        }

        // Helper: Encode a number as a Bitcoin varint (for numbers < 0xffffffff).
        function varIntBuffer(n) {
            if (n < 253) {
                return Buffer.from([n]);
            } else if (n <= 0xffff) {
                const buf = Buffer.allocUnsafe(3);
                buf[0] = 253;
                buf.writeUInt16LE(n, 1);
                return buf;
            } else {
                const buf = Buffer.allocUnsafe(5);
                buf[0] = 254;
                buf.writeUInt32LE(n, 1);
                return buf;
            }
        }

        // Helper: Construct the "magic hash" for a Bitcoin Signed Message.
        // It prefixes the message with "Bitcoin Signed Message:\n", encodes the lengths,
        // concatenates everything, and double-hashes the result.
        function magicHash(message) {
            const messageBuffer = Buffer.from(message, 'utf8');
            const prefix = Buffer.from('Bitcoin Signed Message:\n', 'utf8');
            const prefixLength = varIntBuffer(prefix.length);
            const messageLength = varIntBuffer(messageBuffer.length);
            const buffer = Buffer.concat([prefixLength, prefix, messageLength, messageBuffer]);
            return hash256(buffer);
        }

        // Create the magic hash for the message.
        const msgHash = magicHash(message);

        // Sign the hash using secp256k1.ecdsaSign (which returns an object with the signature and recovery id).
        const sigObj = secp256k1.ecdsaSign(msgHash, privateKey);
        // Compute the header byte: 27 + recovery id + (if compressed then 4 else 0).
        const headerByte = 27 + sigObj.recid + (compressed ? 4 : 0);
        // The final signature is the header byte followed by the 64-byte signature.
        const finalSignature = Buffer.concat([Buffer.from([headerByte]), Buffer.from(sigObj.signature)]);

        // Return the signature as a Base64-encoded string.
        return finalSignature.toString('base64');
    }

    async login({ signature, address, publicKey, type = "PHANTOM" }) {
        try {
            console.table("Login data:", {
                address: address,
                signature: signature,
                publicKey: publicKey,
            });
            // if (!this.anonimousActor) {
            //     await this.initializAnonimousActor();
            // }
            // Generate a new identity for the session.
            const generatedIdentity = Ed25519KeyIdentity.generate();
            const identityPublicKey = generatedIdentity.getPublicKey().toDer();
            // console.log("Generated identity:", generatedIdentity.toJSON());
            let walletType = { Bip322Simple: null };
            if (type === "XVERSE") {
                walletType = { ECDSA: null };
            }

            // Perform login.
            const loginResult = await this.anonimousActor.siwb_login(
                signature,
                address,
                publicKey,
                new Uint8Array(identityPublicKey),
                walletType,
            );
            console.log("Login result:", loginResult);

            // Get delegation.
            const delegation = await this.anonimousActor.siwb_get_delegation(
                address,
                new Uint8Array(identityPublicKey),
                loginResult.Ok.expiration
            );
            // console.log("Delegation result:", delegation);

            // Create the delegation chain.
            const delegationChain = this._createDelegationChain(
                delegation.Ok,
                loginResult.Ok.user_canister_pubkey
            );
            // console.log("DelegationChain:", delegationChain);

            // Reconstruct the identity from the generated identity and delegation chain.
            const sessionIdentityJson = generatedIdentity.toJSON();
            const delegationChainJson = delegationChain.toJSON();
            const secretKeyHex = sessionIdentityJson[1];
            const secretKeyUint8 = this._hexToUint8Array(secretKeyHex);
            const baseIdentity = Ed25519KeyIdentity.fromSecretKey(secretKeyUint8);
            const finalDelegationChain = DelegationChain.fromJSON(delegationChainJson);
            const delegIdentity = new DelegationIdentity(baseIdentity, finalDelegationChain);
            console.log(
                "Loaded identity. Your principal is:",
                delegIdentity.getPrincipal().toText()
            );

            // Authenticate using the delegation chain.
            const rest = await this.odinAuth(delegationChainJson, delegIdentity);
            // console.log("delegIdentity:", delegIdentity);
            // const dataToSave = {
            //     address: address,
            //     generatedIdentity: generatedIdentity,
            //     delegationChain: finalDelegationChain.delegations,
            //     key: key,
            // }

            return {
                userId: delegIdentity.getPrincipal().toText(),
                serializeIdentity: this.serializeData(
                    address,
                    generatedIdentity,
                    delegationChain,
                ),
                type:"BTC",
                name:"ODIN_MAIN_WALLET",
                isFavorite: true,
                _id:"ODIN_MAIN_WALLET",
            };
        } catch (error) {
            console.error("Error during login:", error);
            throw error;
        }
    }

    generateRadomIcpAddress() {
        const originalIdentity = Ed25519KeyIdentity.generate();
        const originalPrincipal = originalIdentity.getPrincipal().toText();
        const identityJson = originalIdentity.toJSON(); // [publicKeyHex, secretKeyHex]

        return identityJson[1];

    }


    async authIdentity(identity) {

        try {
            const unixTimestamp = Date.now(); // Gets Unix timestamp in milliseconds
            const timestampString = unixTimestamp.toString();
            const timestampBuffer = Buffer.from(timestampString, 'utf8'); // Data to sign

            const signatureBytes = await identity.sign(timestampBuffer);

            // Convert the signature to Base64 for easy reading.
            const signatureBase64 = Buffer.from(signatureBytes).toString("base64");

            // 3. Construct the request payload
            const authRequest = {
                publickey: Buffer.from(identity.getPublicKey().toDer()).toString("base64"),
                timestamp: timestampString,
                signature: signatureBase64,
                referrer: "" // Hardcoded referrer from C# code
            };
            console.log("Auth Request:", authRequest);

            const jsonData = JSON.stringify(authRequest);
            const apiUrl = "https://api.odin.fun/v1/auth";

            // 4. Make the POST request using axios
            const response = await axios.post(apiUrl, jsonData, {
                headers: {
                    'Content-Type': 'application/json'
                },
                // Optional: Validate status code explicitly if needed, though axios throws on non-2xx by default
                // validateStatus: function (status) {
                //   return status >= 200 && status < 300; // default
                // }
            });

            // Axios resolves successfully only for 2xx status codes by default
            console.log("Response received:", response.data); // response.data already contains the parsed JSON object
            return response.data; // Assuming response.data matches the AuthToken structure

        } catch (error) {
            if (axios.isAxiosError(error)) {
                // Handle errors specifically from the HTTP request (e.g., non-2xx status)
                console.error(`Error: ${ error.response?.status }, Body:`, error.response?.data || error.message);
            } else {
                // Handle other errors (e.g., crypto issues, setup issues)
                console.error("Exception occurred:", error.message);
            }
            return null; // Return null on any failure
        }
    }

    serializeData = (je, Ye, vr, Er) => {
        return JSON.stringify({
            address: je,
            sessionIdentity: Ye.toJSON(),
            delegationChain: vr.toJSON(),
            providerKey: "test"
        });
    };

    hexToUint8Array(hex) {
        if (hex.startsWith("0x")) {
            hex = hex.slice(2);
        }
        const arr = new Uint8Array(hex.length / 2);
        for (let i = 0; i < hex.length; i += 2) {
            arr[i / 2] = parseInt(hex.substr(i, 2), 16);
        }
        return arr;
    }



    _asSignature = (signature) => {
        const Ye = signature.buffer;
        return (Ye.__signature__ = void 0), Ye;
    };

    _createDelegationChain = (delegation, UserCanisterPubKey) => {
        const Ye = [
            {
                delegation: new Delegation(
                    delegation.delegation.pubkey.buffer,
                    delegation.delegation.expiration,
                    delegation.delegation.targets[0]
                ),
                signature: this._asSignature(delegation.signature),
            },
        ];
        return DelegationChain.fromDelegations(
            Ye,
            this._asDerEncodedPublicKey(UserCanisterPubKey)
        );
    };

    _asDerEncodedPublicKey = (UserCanisterPubKey) => {
        const Ye = UserCanisterPubKey.buffer;
        return (Ye.__derEncodedPublicKey__ = void 0), Ye;
    };
    _hexToUint8Array(hex) {
        if (hex.startsWith("0x")) {
            hex = hex.slice(2);
        }
        const arr = new Uint8Array(hex.length / 2);
        for (let i = 0; i < hex.length; i += 2) {
            arr[i / 2] = parseInt(hex.substr(i, 2), 16);
        }
        return arr;
    }

    _fromBase64 = (base64Str) => {
        const buf = Buffer.from(base64Str, "base64");
        // Return an ArrayBuffer that exactly represents the decoded data
        return buf.buffer.slice(buf.byteOffset, buf.byteOffset + buf.byteLength);
    };

    async odinAuth(delegation, identity, authorization = "") {
        try {
            // console.log("odinAuth delegation:", delegation);
            // console.log("odinAuth identity:", identity);
            const timestamp = Date.now().toString();
            const signature = await identity.sign(
                new TextEncoder().encode(`${ timestamp }`)
            );
            let dataBody = {};
            if (authorization) {
                dataBody = {
                    publickey: Buffer.from(identity.getPublicKey().toDer()).toString("base64"),
                };
            } else {
                dataBody = {
                    delegation: JSON.stringify(delegation),
                };
            }
            // console.log("odinAuth signature:", signature);
            let data = JSON.stringify({
                ...dataBody,
                timestamp: timestamp,
                signature: Buffer.from(signature).toString("base64"),
                referrer: authorization ? "" : "2",
            });
            let config = {
                method: "post",
                maxBodyLength: Infinity,
                url: "https://api.odin.fun/v1/auth",
                headers: {
                    accept: "*/*",
                    "accept-language": "en-US,en;q=0.9",
                    authorization: `Bearer ${ authorization }`,
                    "cache-control": "no-cache",
                    "content-type": "application/json",
                    origin: "https://odin.fun",
                    pragma: "no-cache",
                    priority: "u=1, i",
                    referer: "https://odin.fun/",
                    "sec-ch-ua":
                        '"Not A(Brand";v="8", "Chromium";v="132", "Google Chrome";v="132"',
                    "sec-ch-ua-mobile": "?0",
                    "sec-ch-ua-platform": '"macOS"',
                    "sec-fetch-dest": "empty",
                    "sec-fetch-mode": "cors",
                    "sec-fetch-site": "same-site",
                    'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/114.0.0.0 Safari/537.36',
   },
                data: data,
            };
            const response = await axios.request(config);
            console.log("odinAuth response:", response.data);
            return response.data;
        } catch (error) {
            console.error("Error calling odinAuth:", error);
        }
    }

    async getOdinUserData(userId, authorization) {
        try {

            let config = {
                method: 'get',
                maxBodyLength: Infinity,
                url: 'https://api.odin.fun/v1/user/' + userId,
                headers: {
                    'accept': '*/*',
                    'accept-language': 'en-US,en;q=0.9',
                    'authorization': `Bearer ${ authorization }`,
                    'cache-control': 'no-cache',
                    'origin': 'https://odin.fun',
                    'pragma': 'no-cache',
                    'priority': 'u=1, i',
                    'referer': 'https://odin.fun/',
                    'sec-ch-ua': '"Chromium";v="134", "Not:A-Brand";v="24", "Google Chrome";v="134"',
                    'sec-ch-ua-mobile': '?0',
                    'sec-ch-ua-platform': '"macOS"',
                    'sec-fetch-dest': 'empty',
                    'sec-fetch-mode': 'cors',
                    'sec-fetch-site': 'same-site',
                    'user-agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/134.0.0.0 Safari/537.36'
                }
            };
            const response = await axios.request(config);
            console.log("getOdinUserData response:", response.data);
            if (response.data.btc_deposit_address) {
                return response.data;
            }
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return this.getOdinUserData(userId, authorization);
        } catch (error) {
            console.error("Error calling getOdinUserData:", error);
        }
    }
}


export default IPCAuth;
// fetch("https://api.odin.fun/v1/user/4ldx4-yfq7b-cmz25-ilqlp-6rpoz-enzjs-mm7oz-dzscy-24kf3-yuaur-xae", {
//     "headers": {
//         "accept": "*/*",
//         "accept-language": "en-US,en;q=0.9",
//         "authorization": "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyIjoiNGxkeDQteWZxN2ItY216MjUtaWxxbHAtNnJwb3otZW56anMtbW03b3otZHpzY3ktMjRrZjMteXVhdXIteGFlIiwiaWF0IjoxNzQyNzA1MDYxLCJleHAiOjE3NDI3OTE0NjF9.ZyI50cOzGOwKWwkTbv59TxkYf_Qn8v1iHx7FiQC59fg",
//         "cache-control": "no-cache",
//         "pragma": "no-cache",
//         "priority": "u=1, i",
//         "sec-ch-ua": "\"Chromium\";v=\"134\", \"Not:A-Brand\";v=\"24\", \"Google Chrome\";v=\"134\"",
//         "sec-ch-ua-mobile": "?0",
//         "sec-ch-ua-platform": "\"macOS\"",
//         "sec-fetch-dest": "empty",
//         "sec-fetch-mode": "cors",
//         "sec-fetch-site": "same-site",
//         "Referer": "https://odin.fun/",
//         "Referrer-Policy": "strict-origin-when-cross-origin"
//     },
//     "body": null,
//     "method": "GET"
// });